// Presentation guards. Backend authorization and concurrency remain server responsibilities.
export const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
export const text = (value, limit = 500) => typeof value === 'string' ? value.slice(0, limit) : '';
export const safeIdentifier = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,256}$/.test(value);
export const finitePositive = value => typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
export function coordinates(lat, lng) {
  return typeof lat === 'number' && Number.isFinite(lat) && Math.abs(lat) <= 90 &&
    typeof lng === 'number' && Number.isFinite(lng) && Math.abs(lng) <= 180;
}
export function ageSeconds(value, now = Date.now()) {
  if (typeof value !== 'string' || !/T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return null;
  const date = Date.parse(value);
  return Number.isFinite(date) && date <= now ? Math.floor((now - date) / 1000) : null;
}
export function safePhone(value) {
  if (typeof value !== 'string' || !/^\+?[\d ()-]+$/.test(value)) return null;
  const clean = value.replace(/[ ()-]/g, '');
  return /^\+?\d{7,15}$/.test(clean) ? `tel:${clean}` : null;
}
export function evidencePath(value) {
  if (typeof value !== 'string' || value.length > 500) return null;
  const parts = value.split('/');
  return parts.length && parts.every(p => /^[A-Za-z0-9_-][A-Za-z0-9_.-]*$/.test(p) && p !== '.' && p !== '..')
    ? parts.map(encodeURIComponent).join('/') : null;
}
export function safeReviewUrl(value) {
  try {
    if (typeof value !== 'string' || value.length > 2048) return null;
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password) return null;
    return url.href;
  } catch { return null; }
}
export function localNotificationPath(value) {
  if (typeof value !== 'string' || value.length > 2048 || !value.startsWith('/') || /[\\\s\u0000-\u001f\u007f]/.test(value)) return '/admin/notifications';
  let decoded = value;
  try {
    for (let i = 0; i < 4; i++) {
      if (!decoded.startsWith('/') || decoded.startsWith('//') || /[\\\u0000-\u0020\u007f]/.test(decoded)) return '/admin/notifications';
      const next = decodeURIComponent(decoded);
      if (next === decoded) {
        const url = new URL(decoded, 'https://ca.invalid');
        if (url.origin !== 'https://ca.invalid') return '/admin/notifications';
        const original = new URL(value, 'https://ca.invalid');
        return original.pathname + original.search + original.hash;
      }
      decoded = next;
    }
  } catch { /* malformed escapes are not navigation */ }
  return '/admin/notifications';
}
export function ratingValue(value) {
  if (!record(value) || !Number.isInteger(value.stars) || value.stars < 1 || value.stars > 5) return null;
  return { stars: value.stars, comment: text(value.comment, 2000) };
}
export function routePolyline(value) {
  if (typeof value !== 'string' || !value || value.length > 100000 || !/^[?-~]+$/.test(value)) return null;
  let i = 0, lat = 0, lng = 0;
  const read = () => {
    let result = 0, shift = 0, b;
    do { if (i >= value.length || shift > 30) throw Error('polyline'); b = value.charCodeAt(i++) - 63; result |= (b & 31) << shift; shift += 5; } while (b >= 32);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  try { while (i < value.length) { lat += read(); lng += read(); if (!coordinates(lat / 1e5, lng / 1e5)) return null; } return value; } catch { return null; }
}
export function trackingValue(value) {
  if (!record(value) || !text(value.service_number, 100) || !text(value.status, 80)) throw Error('invalid_tracking');
  const data = {};
  for (const key of ['service_number', 'status', 'vehicle', 'service_type', 'pickup_address', 'destination_address', 'signature_name', 'signed_at']) data[key] = text(value[key]);
  for (const prefix of ['pickup', 'destination']) {
    const lat = value[`${prefix}_lat`], lng = value[`${prefix}_lng`];
    data[`${prefix}_lat`] = coordinates(lat, lng) ? lat : null;
    data[`${prefix}_lng`] = coordinates(lat, lng) ? lng : null;
  }
  data.route_duration_min = finitePositive(value.route_duration_min);
  data.eta_min = finitePositive(value.eta_min);
  data.route_polyline = routePolyline(value.route_polyline);
  data.route_source = value.route_source === 'google_routes' ? value.route_source : null;
  data.route_endpoints = Array.isArray(value.route_endpoints) && value.route_endpoints.length === 4 &&
    coordinates(value.route_endpoints[0], value.route_endpoints[1]) && coordinates(value.route_endpoints[2], value.route_endpoints[3]) ? [...value.route_endpoints] : null;
  data.route_geometry_status = text(value.route_geometry_status, 40);
  data.driver = record(value.driver) ? { name: text(value.driver.name, 150), phone: text(value.driver.phone, 40), photo_url: safeReviewUrl(value.driver.photo_url) } : null;
  data.truck = record(value.truck) ? {
    unit_number: text(value.truck.unit_number, 80), plate: text(value.truck.plate, 30),
    lat: coordinates(value.truck.lat, value.truck.lng) ? value.truck.lat : null,
    lng: coordinates(value.truck.lat, value.truck.lng) ? value.truck.lng : null,
    location_updated_at: text(value.truck.location_updated_at, 80),
  } : null;
  // Cola y precio (b3a12a5): validados, nunca inventados.
  data.queued = value.dispatch_queue_state === 'queued';
  data.queue_position = Number.isInteger(value.queue_position) && value.queue_position > 0 && value.queue_position < 1000 ? value.queue_position : null;
  const priceRaw = value.estimated_price ?? (record(value.price_breakdown) ? value.price_breakdown.total : null);
  data.estimated_price = typeof priceRaw === 'number' && Number.isFinite(priceRaw) && priceRaw >= 0 && priceRaw < 1e7 ? priceRaw : null;
  data.price_currency = record(value.price_breakdown) && /^[A-Z]{3}$/.test(value.price_breakdown.currency || '') ? value.price_breakdown.currency : 'DOP';
  data.price_confirmed = value.price_confirmed === true;
  data.rating = ratingValue(value.rating);
  data.rating_invalid = value.rating != null && data.rating === null;
  const terminal = ['entregado', 'completado'].includes(data.status);
  for (const key of ['photos_before', 'photos_after']) data[key] = terminal && Array.isArray(value[key])
    ? [...new Set(value[key].map(evidencePath).filter(Boolean))].slice(0, 100) : [];
  data.signature_url = terminal ? evidencePath(value.signature_url) : null;
  data.is_demo = value.is_demo === true;
  return data;
}
export function notificationValue(value) {
  if (!record(value) || !['service','accounting','security'].includes(value.source) || !safeIdentifier(value.id) ||
      typeof value.title !== 'string' || !value.title || typeof value.read !== 'boolean') throw Error('invalid_notification');
  return { id: value.id, source: value.source, title: text(value.title), description: text(value.description, 2000),
    read: value.read, created_at: text(value.created_at, 80), link: localNotificationPath(value.link) };
}
export function notificationFeed(list, counts) {
  if (!record(list) || !Array.isArray(list.items) || list.items.length > 300 || !record(counts) ||
      !Number.isSafeInteger(counts.unread) || counts.unread < 0) throw Error('invalid_notifications');
  const items = list.items.map(notificationValue), keys = new Set();
  for (const n of items) { const key = `${n.source}:${n.id}`; if (keys.has(key)) throw Error('duplicate_notification'); keys.add(key); }
  const by_source = {};
  if (counts.by_source != null) {
    if (!record(counts.by_source)) throw Error('invalid_counts');
    for (const source of ['service','accounting','security']) {
      const count = counts.by_source[source];
      if (count != null && (!Number.isSafeInteger(count) || count < 0)) throw Error('invalid_counts');
      if (count != null) by_source[source] = count;
    }
  }
  return { items, counts: { unread: counts.unread, by_source } };
}
export function displayDate(value) {
  if (ageSeconds(value) === null) return 'Fecha no disponible';
  return new Date(value).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' });
}
